import React, { useState, useRef, useEffect } from 'react';
import { Link, useNavigate, useOutletContext, useLocation } from 'react-router-dom';
import { ArrowLeft, Mail, RefreshCw, AlertCircle } from 'lucide-react';
import api from '../api';
import { useLanguage } from '../context/LanguageContext';
import { RegisterContextType } from './RegisterLayout';
import BrandLogo from '../components/BrandLogo';
import { authLink, authReturnPath } from '../utils/authReturnPath';

export default function VerificationPage() {
  const { t, language } = useLanguage();
  const navigate = useNavigate();
  const location = useLocation();
  const [redirectTo] = useState(() => authReturnPath(location.search, location.state));
  const { formData } = useOutletContext<RegisterContextType>();

  // Email from registration flow (context) or from login redirect (location.state)
  const userEmail = formData.email || (location.state as any)?.email || '';

  const [code, setCode] = useState(['', '', '', '', '', '']);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');
  const [resendCooldown, setResendCooldown] = useState(0);
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  useEffect(() => {
    // No email from either source — redirect appropriately
    if (!userEmail) {
      navigate(authLink('/register', redirectTo));
    }
  }, [userEmail, navigate, redirectTo]);

  useEffect(() => {
    let timer: NodeJS.Timeout;
    if (resendCooldown > 0) {
      timer = setInterval(() => setResendCooldown(c => c - 1), 1000);
    }
    return () => clearInterval(timer);
  }, [resendCooldown]);

  const handleChange = (index: number, value: string) => {
    if (value.length > 1) {
      // Handle paste
      const pastedCode = value.slice(0, 6).split('');
      const newCode = [...code];
      pastedCode.forEach((char, i) => {
        if (index + i < 6) newCode[index + i] = char;
      });
      setCode(newCode);
      
      // Focus last filled input
      const focusIndex = Math.min(index + pastedCode.length, 5);
      inputRefs.current[focusIndex]?.focus();
      return;
    }

    const newCode = [...code];
    newCode[index] = value;
    setCode(newCode);

    // Auto-advance
    if (value !== '' && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace' && code[index] === '' && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const verificationCode = code.join('');
    if (verificationCode.length !== 6) return;
    
    setIsSubmitting(true);
    setErrorMsg('');
    try {
      await api.post('/auth/verify', {
        email: userEmail,
        code: verificationCode
      });
      // Optionally login user automatically, or send them to login page
      navigate(authLink('/login', redirectTo), { state: { message: language === 'kk' ? 'Электрондық пошта расталды. Жүйеге кіріңіз.' : 'Эл. почта успешно подтверждена. Войдите в систему.' } });
    } catch (error: any) {
      if (error.response?.status === 429) {
        // Handle penalty visually or simply show translated too many requests
        setErrorMsg(t('errors.too_many_requests'));
      } else {
        setErrorMsg(t('errors.invalid_code'));
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleResend = async () => {
    if (resendCooldown > 0) return;
    
    try {
      await api.post('/auth/resend-verification', { email: userEmail });
      setResendCooldown(15); // Default 15s wait
      setErrorMsg('');
    } catch (error: any) {
      if (error.response?.status === 429) {
        setErrorMsg(t('errors.too_many_requests'));
        setResendCooldown(60); 
      } else {
        setErrorMsg(t('errors.default'));
      }
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8 font-sans relative overflow-hidden">
      {/* Background decorations matching the other auth pages */}
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden z-0 pointer-events-none">
        <div className="absolute -top-40 -right-40 w-96 h-96 bg-goszakup-600/10 rounded-full blur-3xl"></div>
        <div className="absolute top-1/2 -left-20 w-80 h-80 bg-blue-500/10 rounded-full blur-3xl"></div>
      </div>

      <div className="sm:mx-auto sm:w-full sm:max-w-md relative z-10">
        <div className="flex justify-center mb-6">
          <Link to="/" className="flex items-center gap-3 text-goszakup-600 hover:text-goszakup-700 transition-colors group">
            <div className="bg-white p-3 rounded-2xl shadow-sm border border-gray-100 group-hover:shadow-md transition-all">
              <BrandLogo variant="full" className="h-24 w-auto" />
            </div>
          </Link>
        </div>
      </div>

      <div className="mt-2 sm:mx-auto sm:w-full sm:max-w-md relative z-10">
        <div className="bg-white py-10 px-4 shadow-xl shadow-gray-200/50 sm:rounded-3xl sm:px-10 border border-gray-100">
          
          <div className="text-center mb-8">
            <div className="mx-auto w-16 h-16 bg-blue-50 rounded-full flex items-center justify-center mb-4">
              <Mail className="w-8 h-8 text-goszakup-600" />
            </div>
            <h2 className="text-2xl font-extrabold text-gray-900">
              Подтверждение почты
            </h2>
            <p className="mt-2 text-sm text-gray-500">
              Мы отправили 6-значный код на <br />
              <span className="font-medium text-gray-900">{userEmail}</span>
            </p>
          </div>

          <form className="space-y-8" onSubmit={handleSubmit}>
            {errorMsg && (
              <div className="bg-red-50 text-red-600 p-3 rounded-xl flex items-center gap-2 text-sm border border-red-100">
                <AlertCircle className="w-4 h-4 flex-shrink-0" />
                <span>{errorMsg}</span>
              </div>
            )}
            <div className="flex justify-between gap-2 sm:gap-3">
              {code.map((digit, index) => (
                <input
                  key={index}
                  ref={(el) => { inputRefs.current[index] = el; }}
                  type="text"
                  maxLength={6}
                  value={digit}
                  onChange={(e) => handleChange(index, e.target.value)}
                  onKeyDown={(e) => handleKeyDown(index, e)}
                  className="w-12 h-14 sm:w-14 sm:h-16 text-center text-2xl font-bold text-gray-900 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-goszakup-500 focus:bg-white transition-all shadow-sm"
                  placeholder="-"
                />
              ))}
            </div>

            <div className="flex flex-col gap-4">
              <button
                type="submit"
                disabled={code.join('').length !== 6 || isSubmitting}
                className="w-full flex justify-center py-3 px-4 border border-transparent rounded-xl shadow-sm rounded-xl shadow-goszakup-500/30 text-sm font-semibold text-white bg-goszakup-600 hover:bg-goszakup-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-goszakup-500 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSubmitting ? 'Проверка...' : 'Подтвердить'}
              </button>

              <button
                type="button"
                onClick={() => navigate('/register')}
                className="w-full flex justify-center items-center gap-2 py-3 px-4 border border-gray-200 rounded-xl text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-200 transition-all"
              >
                <ArrowLeft className="w-4 h-4" />
                Вернуться назад
              </button>
            </div>
          </form>

          <div className="mt-8 text-center text-sm">
            <span className="text-gray-500">Не получили код? </span>
            <button 
              type="button"
              onClick={handleResend}
              disabled={resendCooldown > 0}
              className="font-medium text-goszakup-600 hover:text-goszakup-500 transition-colors inline-flex items-center gap-1 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <RefreshCw className={`w-3 h-3 ${resendCooldown > 0 ? 'animate-spin' : ''}`} />
              {resendCooldown > 0 ? `Отправить снова через ${resendCooldown}с` : 'Отправить снова'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
